import { useExpanded, useFlexLayout, usePagination, useResizeColumns, useTable } from "react-table";
import classnames from "classnames";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CustomizedSelect from "../Input/CustomizedSelect";
import { Fragment } from "react";
import { DEFAULT_PAGE_SIZE } from "../../constants";

const ReactTable = (props) => {
    const { className = "", data, getTdProps = () => {}, trClassName = "", SubComponent } = props;

    const columnsFromProps = props.columns;

    /* Defining default column width for every column */
    const defaultColumn = {
        minWidth: 10,
        width: 20,
        maxWidth: 40,
    };

    /* If the table is expandable then add the expander column */
    if (SubComponent) {
        columnsFromProps.unshift({
            Header: "",
            id: "expander",
            resizable: false,
            className: "text-center",
            Cell: ({ row }) => {
                return (
                    //Applying the toggle expander props i.e onClick, style and title
                    <div
                        {...row.getToggleRowExpandedProps()}
                        title="Click here to see more information"
                        className="rt-td rt-expandable p-0"
                    >
                        <div className={classnames("rt-expander", row.isExpanded ? "-open" : "")}>•</div>
                    </div>
                );
            },
        });
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state: { pageIndex, pageSize },
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        columns,
    } = useTable(
        {
            columns: columnsFromProps,
            data,
            defaultColumn,
            SubComponent,
            initialState: { pageSize: props.pageSize ?? DEFAULT_PAGE_SIZE },
        },
        useFlexLayout,
        useResizeColumns,
        useExpanded,
        usePagination
    );

    return (
        //Applying the table props
        <TableContainer component={Paper} className={classnames(className, "ReactTable")}>
            <Table {...getTableProps} className="rt-table" sx={{ minWidth: 300 }} aria-label="simple table">
                <TableHead className="rt-thead-header">
                    {
                        // Looping over the header rows
                        headerGroups.map((headerGroup, index) => (
                            //Applying the header row props
                            <TableRow {...headerGroup.getHeaderGroupProps()} className="rt-tr" key={index}>
                                {
                                    //Looping over the headers in each row
                                    headerGroup.headers.map((column) => {
                                        /*Checking if column can be resized and applying resizable props along with
                                        header cell props and finally rendering the header cell
                                        */
                                        return column.resizable === false ? (
                                            <TableCell
                                                {...column.getHeaderProps()}
                                                className={classnames("rt-th table-cell", column.className)}
                                            >
                                                {column.render("Header")}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                {...column.getHeaderProps()}
                                                className={classnames(
                                                    "rt-th rt-resizable-header table-cell",
                                                    column.className
                                                )}
                                            >
                                                <div className="rt-resizable-header-content">
                                                    {column.render("Header")}
                                                </div>
                                                <div {...column.getResizerProps()} className="rt-resizer" />
                                            </TableCell>
                                        );
                                    })
                                }
                            </TableRow>
                        ))
                    }
                </TableHead>
                <TableBody {...getTableBodyProps()} className="rt-tbody rt-tr-group">
                    {
                        /* Applying the table body props*/
                        //Loopting over the table rows
                        page.map((row, ind) => {
                            //Preparing row for rendering
                            prepareRow(row);
                            return (
                                <Fragment key={row.id}>
                                    {/*Applying the row props and adding classname for alternate rows*/}
                                    <TableRow
                                        {...row.getRowProps()}
                                        onClick={props.getTrProps ? props.getTrProps(row).onClick : undefined}
                                        className={classnames("rt-tr", ind % 2 === 0 ? "-odd" : "-even", trClassName)}
                                    >
                                        {
                                            //Looping over the row cells
                                            row.cells.map((cell, cellIndex) => {
                                                return (
                                                    //Applying the cell props and rendering the cell content
                                                    <TableCell
                                                        {...getTdProps(cell)}
                                                        {...cell.getCellProps()}
                                                        className={classnames(
                                                            "rt-td",
                                                            cell.column.className,
                                                            "table-cell"
                                                        )}
                                                        key={cellIndex}
                                                    >
                                                        {cell.render("Cell")}
                                                    </TableCell>
                                                );
                                            })
                                        }
                                    </TableRow>
                                    {/* Checking row expansion */}
                                    {row.isExpanded ? SubComponent({ row }) : null}
                                </Fragment>
                            );
                        })
                    }
                    {[...Array(Math.min(pageSize - page.length, props.minRows)).keys()].map((index) => {
                        return (
                            <TableRow
                                key={index}
                                className={classnames("rt-tr", index % 2 === 0 ? "-odd" : "-even", trClassName)}
                            >
                                {[...Array(columns.length).keys()].map((j, cellIndex) => (
                                    <TableCell
                                        style={{ width: columns[j].width }}
                                        className={classnames("rt-td", "table-cell")}
                                        key={cellIndex}
                                    >
                                        &nbsp;
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {props.withPagination ? (
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {"<<"}
                    </button>{" "}
                    <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {"<"}
                    </button>{" "}
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                        {">"}
                    </button>{" "}
                    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        {">>"}
                    </button>{" "}
                    <span>
                        Page{" "}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{" "}
                    </span>
                    <span>
                        | Aller à la page:{" "}
                        <input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(page);
                            }}
                            style={{ width: "100px" }}
                        />
                    </span>{" "}
                    <CustomizedSelect
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Afficher {pageSize}
                            </option>
                        ))}
                    </CustomizedSelect>
                </div>
            ) : null}
        </TableContainer>
    );
};

export default ReactTable;
